import { parseAttendanceDateTime } from "@/utils/attendanceDateTime";

function parseWhen(value) {
  return parseAttendanceDateTime(value);
}

function formatTick(date) {
  return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

function buildHourTicks(shiftStart, shiftEnd) {
  const ticks = [];
  const cursor = new Date(shiftStart);
  cursor.setMinutes(0, 0, 0);

  while (cursor.getTime() <= shiftEnd.getTime()) {
    if (cursor.getTime() >= shiftStart.getTime() - 60 * 60 * 1000) {
      ticks.push(formatTick(cursor));
    }
    cursor.setHours(cursor.getHours() + 1);
  }

  if (!ticks.length) {
    ticks.push(formatTick(shiftStart), formatTick(shiftEnd));
  }

  return ticks;
}

function pctBetween(ms, windowStart, windowMs) {
  if (!windowMs) return 0;
  return Math.max(0, Math.min(100, ((ms - windowStart) / windowMs) * 100));
}

/**
 * Build a same-day timeline bar from shift window + punch record.
 *
 * @param {{
 *   shiftStart?: Date | null,
 *   shiftEnd?: Date | null,
 *   breakMinutes?: number,
 *   record?: object | null,
 *   productiveMinutes?: number,
 *   now?: Date,
 * }} options
 */
export function buildAttendanceDayTimeline(options = {}) {
  const shiftStart = parseWhen(options.shiftStart);
  const shiftEnd = parseWhen(options.shiftEnd);
  const now = parseWhen(options.now) || new Date();
  const record = options.record || null;
  const configuredBreak = Number(options.breakMinutes) || 0;

  if (!shiftStart || !shiftEnd || shiftEnd <= shiftStart) {
    return {
      empty: true,
      message: "No shift window configured for today.",
      segments: [],
      ticks: [],
      totalSessionMinutes: 0,
      productiveMinutes: 0,
      breakMinutes: 0,
      overtimeMinutes: 0,
    };
  }

  const windowMs = shiftEnd.getTime() - shiftStart.getTime();
  const ticks = buildHourTicks(shiftStart, shiftEnd);
  const checkIn = parseWhen(record?.check_in_at);

  if (!checkIn) {
    return {
      empty: true,
      message: "Timeline appears after you clock in.",
      segments: [{ class: "bg-secondary bg-opacity-25", widthPct: 100 }],
      ticks,
      totalSessionMinutes: 0,
      productiveMinutes: 0,
      breakMinutes: 0,
      overtimeMinutes: 0,
    };
  }

  const checkOut = parseWhen(record?.check_out_at);
  const endPoint = checkOut || now;
  const breakMinutes = Number(record?.break_minutes) || 0;
  const overtimeMinutes = Number(record?.overtime_minutes) || 0;
  const sessionMinutes = Math.max(
    0,
    Math.floor((endPoint.getTime() - checkIn.getTime()) / 60000),
  );
  const productiveMinutes =
    Number(record?.total_work_minutes) ||
    Number(options.productiveMinutes) ||
    Math.max(0, sessionMinutes - breakMinutes);

  const segments = [];
  const push = (cls, widthPct) => {
    if (widthPct > 0.4) segments.push({ class: cls, widthPct });
  };

  const checkInMs = checkIn.getTime();
  const endMs = endPoint.getTime();
  const shiftStartMs = shiftStart.getTime();
  const shiftEndMs = shiftEnd.getTime();

  // Before punch-in (within scheduled shift)
  if (checkInMs > shiftStartMs) {
    push(
      "bg-light border",
      pctBetween(checkInMs, shiftStartMs, windowMs) -
        pctBetween(shiftStartMs, shiftStartMs, windowMs),
    );
  }

  // Worked portion inside the scheduled shift window
  const workEndMs = Math.min(endMs, shiftEndMs);
  if (workEndMs > checkInMs) {
    const workSpanPct =
      pctBetween(workEndMs, shiftStartMs, windowMs) -
      pctBetween(checkInMs, shiftStartMs, windowMs);
    const workSpanMinutes = Math.max(
      1,
      Math.floor((workEndMs - checkInMs) / 60000),
    );
    const breakShare = Math.min(breakMinutes / workSpanMinutes, 1);
    const productiveShare = Math.max(0, 1 - breakShare);

    push("bg-success rounded me-1", workSpanPct * productiveShare);
    push("bg-warning rounded me-1", workSpanPct * breakShare);
  }

  // Remaining scheduled shift (not yet reached while still clocked in)
  if (!checkOut && endMs < shiftEndMs) {
    push(
      "bg-secondary bg-opacity-10",
      pctBetween(shiftEndMs, shiftStartMs, windowMs) -
        pctBetween(endMs, shiftStartMs, windowMs),
    );
  }

  // Overtime beyond scheduled shift end
  if (endMs > shiftEndMs && overtimeMinutes > 0) {
    const otSpanMinutes = Math.max(
      overtimeMinutes,
      Math.floor((endMs - shiftEndMs) / 60000),
    );
    const otPct = Math.min(
      30,
      (otSpanMinutes / (windowMs / 60000)) * 100,
    );
    push("bg-info rounded me-1", otPct);
  }

  const totalPct = segments.reduce((sum, s) => sum + s.widthPct, 0);
  if (totalPct > 100 && totalPct > 0) {
    segments.forEach((s) => {
      s.widthPct = (s.widthPct / totalPct) * 100;
    });
  }

  return {
    empty: false,
    message: "",
    segments,
    ticks,
    totalSessionMinutes: sessionMinutes,
    productiveMinutes,
    breakMinutes,
    overtimeMinutes,
    configuredBreakMinutes: configuredBreak,
  };
}

export function minutesToShortLabel(m) {
  const total = Math.max(0, Number(m) || 0);
  const h = Math.floor(total / 60);
  const mm = total % 60;
  return `${String(h).padStart(2, "0")}h ${String(mm).padStart(2, "0")}m`;
}

export function breakMinutesLabel(m) {
  const total = Math.max(0, Number(m) || 0);
  if (total < 60) return `${total}m`;
  const h = Math.floor(total / 60);
  const mm = total % 60;
  return mm ? `${h}h ${mm}m` : `${h}h`;
}
